// My Work Page - View and manage assigned work items
import React, { useState, useMemo, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import AdminHeader from '../../../shared/components/AdminHeader';
import AdminDataTable from '../../../shared/components/AdminDataTable';
import TabBar from '../../../shared/components/TabBar';
import { ReleaseButton } from '../components/ClaimButton';
import SlaIndicator from '../components/SlaIndicator';
import StatusToggle from '../../../shared/components/StatusToggle';
import { OrderStatusPreviewPopover } from '../../orders/components/OrderStatusPreview';

interface MyWorkProps {
  brand: BrandConfig;
  Icons: IconsType;
  onNavigate: (module: string, subSection?: string) => void;
}

type OperatorStatus = 'available' | 'busy' | 'away' | 'offline';

interface WorkItemEntity {
  title: string;
  caseNumber: string;
  court?: string;
  type?: string;
}

interface WorkItem {
  id: string;
  entityType: string;
  entity: WorkItemEntity;
  status: string;
  priority: string;
  receivedAt: string;
  firstResponseAt?: string;
  dueAt: string;
  lockedAt?: string;
  isAtRisk: boolean;
  isBreached: boolean;
  [key: string]: unknown;
}

// Mock colleagues data for Team Coverage feature
const mockColleagues = [
  {
    id: '660e8400-e29b-41d4-a716-446655440002',
    name: 'Operator Two',
    avatar: 'O',
    status: 'busy',
    computedStatus: 'active',
    backlogCount: 4,
    backlog: [
      { id: 'col-item-1', title: 'Smith v. Johnson', priority: 'priority', dueAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString() },
      { id: 'col-item-2', title: 'Contract Review', priority: 'standard', dueAt: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString() },
    ],
  },
  {
    id: '660e8400-e29b-41d4-a716-446655440004',
    name: 'Operator Three',
    avatar: 'O',
    status: 'away',
    computedStatus: 'idle',
    backlogCount: 3,
    backlog: [
      { id: 'col-item-3', title: 'Motion to Dismiss', priority: 'urgent', dueAt: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString() },
      { id: 'col-item-4', title: 'Discovery Request', priority: 'standard', dueAt: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString() },
    ],
  },
  {
    id: '660e8400-e29b-41d4-a716-446655440005',
    name: 'Operator Four',
    avatar: 'O',
    status: 'offline',
    computedStatus: 'offline',
    backlogCount: 2,
    backlog: [
      { id: 'col-item-5', title: 'Filing Correction', priority: 'priority', dueAt: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString() },
    ],
  },
];

// SLA Definitions:
// - Standard: Within 3 business days
// - Priority: Next business day (24 hours from submission)
// - Urgent: Same day (end of day 23:59)
// - On-Demand: Specific time requested by customer

// Helper to get end of today (23:59)
const getEndOfDay = () => {
  const eod = new Date();
  eod.setHours(23, 59, 0, 0);
  return eod;
};

// Mock assigned work data (in production, this comes from API)
const mockMyWork: WorkItem[] = [
  {
    id: '770e8400-e29b-41d4-a716-446655440004',
    entityType: 'order',
    entity: { title: 'Brown v. Taylor', caseNumber: '24STCV00456', court: 'Los Angeles County Superior Court', type: 'court-filing' },
    status: 'claimed',
    priority: 'standard', // Within 3 business days
    receivedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    firstResponseAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    dueAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days from now
    isAtRisk: false,
    isBreached: false,
  },
  {
    id: '770e8400-e29b-41d4-a716-446655440005',
    entityType: 'case',
    entity: { title: 'Garcia v. Martinez', caseNumber: '24STCV00789' },
    status: 'locked',
    priority: 'priority', // 24 hours from submission
    receivedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // Submitted 6h ago
    firstResponseAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    dueAt: new Date(Date.now() - 6 * 60 * 60 * 1000 + 24 * 60 * 60 * 1000).toISOString(), // 24h from submission = 18h remaining
    lockedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    isAtRisk: false,
    isBreached: false,
  },
  {
    id: '770e8400-e29b-41d4-a716-446655440010',
    entityType: 'filing',
    entity: { title: 'Motion Response', caseNumber: '23STCV11111' },
    status: 'claimed',
    priority: 'priority', // 24 hours from submission - BREACHED
    receivedAt: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(), // Submitted 30h ago
    firstResponseAt: new Date(Date.now() - 28 * 60 * 60 * 1000).toISOString(),
    dueAt: new Date(Date.now() - 30 * 60 * 60 * 1000 + 24 * 60 * 60 * 1000).toISOString(), // Was due 6h ago
    isAtRisk: false,
    isBreached: true,
  },
  {
    id: '770e8400-e29b-41d4-a716-446655440011',
    entityType: 'order',
    entity: { title: 'Urgent Filing Request', caseNumber: '24STCV01234' },
    status: 'claimed',
    priority: 'urgent', // End of day 23:59
    receivedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    firstResponseAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    dueAt: getEndOfDay().toISOString(), // Due at 23:59 today
    isAtRisk: getEndOfDay().getTime() - Date.now() < 2 * 60 * 60 * 1000, // At risk if < 2h to EOD
    isBreached: false,
  },
];

// Priority labels and colors aligned with business SLAs
const priorityLabels = {
  standard: 'Standard',
  priority: 'Priority',
  urgent: 'Urgent',
  'on-demand': 'On-Demand',
};

const priorityColors = {
  standard: '#6B7280',   // Gray - default (3 business days)
  priority: '#F59E0B',   // Amber - expedited (next business day)
  urgent: '#EF4444',     // Red - rush (same day)
  'on-demand': '#8B5CF6', // Purple - custom deadline
};

// Sort order: urgent first, then on-demand, priority, standard
const priorityOrder = {
  urgent: 0,
  'on-demand': 1,
  priority: 2,
  standard: 3,
};
const statusLabels = { claimed: 'Claimed', assigned: 'Assigned', locked: 'Locked' };
const statusColors = {
  claimed: '#3B82F6',
  assigned: '#8B5CF6',
  locked: '#059669',
};

// Team Coverage Section Component
const TeamCoverageSection = ({ colleagues, onClaimFromColleague, claimingId, brand, Icons }) => {
  const [expandedColleague, setExpandedColleague] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(null);

  const statusColors = {
    available: '#10B981',
    busy: '#F59E0B',
    away: '#6B7280',
    offline: '#EF4444',
  };

  const activityLabels = {
    active: 'Online now',
    idle: 'Away',
    offline: 'Offline',
  };

  const handleClaimClick = (colleague, item) => {
    if (colleague.computedStatus === 'active') {
      // Show confirmation if colleague is actively working
      setShowConfirmation({ colleague, item });
    } else {
      // Silent claim if colleague is idle/offline
      onClaimFromColleague(item.id, colleague.id);
    }
  };

  const confirmClaim = () => {
    if (showConfirmation) {
      onClaimFromColleague(showConfirmation.item.id, showConfirmation.colleague.id);
      setShowConfirmation(null);
    }
  };

  return (
    <div
      className="rounded-xl p-4 mb-4"
      style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icons.ProcessServing className="w-5 h-5" style={{ color: brand.colors.link }} />
          <h3 className="font-semibold" style={{ color: brand.colors.secondary }}>
            Team Coverage
          </h3>
        </div>
        <span className="text-xs px-2 py-1 rounded-sm" style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}>
          Help colleagues by claiming from their backlog
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {colleagues.map(colleague => (
          <div
            key={colleague.id}
            className="rounded-lg p-3 cursor-pointer transition-all hover:shadow-xs"
            style={{
              backgroundColor: expandedColleague === colleague.id ? `${brand.colors.primary}05` : brand.colors.bgLight,
              border: expandedColleague === colleague.id ? `1px solid ${brand.colors.primary}30` : '1px solid transparent',
            }}
            role="button"
            tabIndex={0}
            onClick={() => setExpandedColleague(expandedColleague === colleague.id ? null : colleague.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedColleague(expandedColleague === colleague.id ? null : colleague.id); } }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                  style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                >
                  {colleague.avatar}
                </div>
                <div>
                  <div className="font-medium text-sm" style={{ color: brand.colors.text }}>
                    {colleague.name}
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: statusColors[colleague.status] }}
                    />
                    <span style={{ color: brand.colors.textLight }}>
                      {activityLabels[colleague.computedStatus]}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-sm"
                  style={{ backgroundColor: `${brand.colors.primary}15`, color: brand.colors.link }}
                >
                  {colleague.backlogCount} items
                </span>
                <Icons.ChevronDown
                  className="w-4 h-4 transition-transform"
                  style={{
                    color: brand.colors.textLight,
                    transform: expandedColleague === colleague.id ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </div>
            </div>

            {/* Expanded backlog items */}
            {expandedColleague === colleague.id && colleague.backlog.length > 0 && (
              <div className="mt-3 pt-3 border-t space-y-2" style={{ borderColor: brand.colors.border }}>
                {colleague.backlog.map(item => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-2 rounded-sm"
                    style={{ backgroundColor: 'white' }}
                    onClick={e => e.stopPropagation()}
                  >
                    <div>
                      <div className="text-sm font-medium" style={{ color: brand.colors.text }}>
                        {item.title}
                      </div>
                      <div className="text-xs" style={{ color: brand.colors.textLight }}>
                        {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)} priority
                      </div>
                    </div>
                    <button
                      onClick={() => handleClaimClick(colleague, item)}
                      disabled={claimingId !== null}
                      className="px-3 py-1 text-xs rounded-sm transition-colors disabled:opacity-50"
                      style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                    >
                      {claimingId === item.id ? 'Claiming...' : 'Claim'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowConfirmation(null)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <h4 className="text-lg font-semibold mb-2" style={{ color: brand.colors.secondary }}>
              Claim from active colleague?
            </h4>
            <p className="text-sm mb-4" style={{ color: brand.colors.textLight }}>
              <strong>{showConfirmation.colleague.name}</strong> is currently active and will be notified that you claimed "{showConfirmation.item.title}" from their backlog.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmation(null)}
                className="px-4 py-2 text-sm rounded-lg border transition-colors"
                style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
              >
                Cancel
              </button>
              <button
                onClick={confirmClaim}
                className="px-4 py-2 text-sm rounded-lg transition-colors"
                style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
              >
                Claim Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MyWork: React.FC<MyWorkProps> = ({ brand, Icons, onNavigate }) => {
  const [activeFilter, setActiveFilter] = useState('active');
  const [myWork, setMyWork] = useState<WorkItem[]>(mockMyWork);
  const [operatorStatus, setOperatorStatus] = useState<OperatorStatus>('available');
  const [releasingId, setReleasingId] = useState(null);
  const [lockingId, setLockingId] = useState(null);
  const [claimingFromColleagueId, setClaimingFromColleagueId] = useState(null);
  const [showTeamCoverage, setShowTeamCoverage] = useState(true);
  const [colleagues, setColleagues] = useState(mockColleagues);

  // Filter tabs
  const activeItems = myWork.filter(i => ['claimed', 'assigned', 'locked'].includes(i.status));
  const filterTabs = [
    { id: 'active', label: 'Active', count: activeItems.length },
    { id: 'locked', label: 'Locked', count: myWork.filter(i => i.status === 'locked').length },
    { id: 'at-risk', label: 'At Risk', count: myWork.filter(i => i.isAtRisk || i.isBreached).length },
  ];

  // Apply filters
  const filteredItems = useMemo(() => {
    let items = [...myWork];

    switch (activeFilter) {
      case 'locked':
        items = items.filter(i => i.status === 'locked');
        break;
      case 'at-risk':
        items = items.filter(i => i.isAtRisk || i.isBreached);
        break;
      case 'active':
      default:
        items = items.filter(i => ['claimed', 'assigned', 'locked'].includes(i.status));
        break;
    }

    // Sort by priority order then due date
    items.sort((a, b) => {
      const aOrder = priorityOrder[a.priority] ?? 99;
      const bOrder = priorityOrder[b.priority] ?? 99;
      if (aOrder !== bOrder) return aOrder - bOrder;
      const aDue = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
      const bDue = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
      return aDue - bDue;
    });

    return items;
  }, [myWork, activeFilter]);

  const handleRelease = async (assignmentId) => {
    setReleasingId(assignmentId);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMyWork(prev => prev.filter(i => i.id !== assignmentId));
    } finally {
      setReleasingId(null);
    }
  };

  const handleLock = async (assignmentId) => {
    setLockingId(assignmentId);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setMyWork(prev => prev.map(i =>
        i.id === assignmentId
          ? { ...i, status: 'locked', lockedAt: new Date().toISOString() }
          : i
      ));
    } finally {
      setLockingId(null);
    }
  };

  const handleUnlock = async (assignmentId) => {
    setLockingId(assignmentId);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setMyWork(prev => prev.map(i =>
        i.id === assignmentId
          ? { ...i, status: 'claimed', lockedAt: undefined }
          : i
      ));
    } finally {
      setLockingId(null);
    }
  };

  const handleComplete = async (assignmentId) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setMyWork(prev => prev.filter(i => i.id !== assignmentId));
    } catch (error) {
      console.error('Error completing:', error);
    }
  };

  // Handle claiming work from a colleague's backlog
  const handleClaimFromColleague = useCallback(async (itemId, colleagueId) => {
    setClaimingFromColleagueId(itemId);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));

      // Find the item in colleague's backlog
      const colleague = colleagues.find(c => c.id === colleagueId);
      const item = colleague?.backlog.find(i => i.id === itemId);

      if (item) {
        // Add to my work
        const newWorkItem: WorkItem = {
          id: itemId,
          entityType: 'order',
          entity: { title: item.title, caseNumber: '' },
          status: 'claimed',
          priority: item.priority,
          receivedAt: new Date().toISOString(),
          dueAt: item.dueAt,
          isAtRisk: false,
          isBreached: false,
        };
        setMyWork(prev => [...prev, newWorkItem]);

        // Remove from colleague's backlog
        setColleagues(prev => prev.map(c =>
          c.id === colleagueId
            ? {
                ...c,
                backlog: c.backlog.filter(i => i.id !== itemId),
                backlogCount: c.backlogCount - 1,
              }
            : c
        ));
      }
    } catch (error) {
      console.error('Error claiming from colleague:', error);
    } finally {
      setClaimingFromColleagueId(null);
    }
  }, [colleagues]);

  const formatTimeRemaining = (dateString) => {
    if (!dateString) return '-';
    const diff = new Date(dateString).getTime() - Date.now();
    if (diff < 0) {
      const overdue = Math.abs(diff);
      const hours = Math.floor(overdue / (1000 * 60 * 60));
      // Show days (rounded down) when > 24 hours overdue
      if (hours >= 24) {
        const days = Math.floor(hours / 24);
        return `${days} day${days > 1 ? 's' : ''} overdue`;
      }
      const minutes = Math.floor((overdue % (1000 * 60 * 60)) / (1000 * 60));
      return hours > 0 ? `${hours}h ${minutes}m overdue` : `${minutes}m overdue`;
    }
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    // Show days (rounded down) when > 24 hours
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  // Table columns
  const columns = [
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span
          className="px-2 py-0.5 rounded-sm text-xs font-medium"
          style={{
            backgroundColor: `${statusColors[value] || '#6B7280'}20`,
            color: statusColors[value] || '#6B7280',
          }}
        >
          {statusLabels[value] || value}
        </span>
      ),
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value) => (
        <span
          className="px-2 py-0.5 rounded-sm text-xs font-medium"
          style={{
            backgroundColor: `${priorityColors[value] || '#6B7280'}20`,
            color: priorityColors[value] || '#6B7280',
          }}
        >
          {priorityLabels[value]}
        </span>
      ),
    },
    {
      key: 'entity',
      label: 'Item',
      render: (entity, row) => (
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <div className="font-medium" style={{ color: brand.colors.text }}>
              {entity?.title || 'Untitled'}
            </div>
            <div className="text-xs" style={{ color: brand.colors.textLight }}>
              {row.entityType.charAt(0).toUpperCase() + row.entityType.slice(1)}
              {entity?.caseNumber && ` - ${entity.caseNumber}`}
            </div>
          </div>
          {row.entityType === 'order' && (
            <OrderStatusPreviewPopover orderId={row.id} brand={brand} />
          )}
        </div>
      ),
    },
    {
      key: 'dueAt',
      label: 'Due',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <SlaIndicator
            dueAt={value}
            isAtRisk={row.isAtRisk}
            isBreached={row.isBreached}
            brand={brand}
          />
          <span
            className="text-sm"
            style={{
              color: row.isBreached ? brand.colors.error : row.isAtRisk ? brand.colors.warning : brand.colors.text,
              fontWeight: row.isAtRisk || row.isBreached ? '600' : '400',
            }}
          >
            {formatTimeRemaining(value)}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {row.status === 'locked' ? (
            <button
              onClick={() => handleUnlock(row.id)}
              disabled={lockingId !== null}
              className="px-2 py-1 text-xs rounded-sm border transition-colors hover:bg-gray-50 disabled:opacity-50"
              style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
            >
              {lockingId === row.id ? 'Unlocking...' : 'Unlock'}
            </button>
          ) : (
            <button
              onClick={() => handleLock(row.id)}
              disabled={lockingId !== null}
              className="px-2 py-1 text-xs rounded-sm transition-colors disabled:opacity-50"
              style={{ backgroundColor: brand.colors.success, color: 'white' }}
            >
              {lockingId === row.id ? 'Locking...' : 'Lock'}
            </button>
          )}
          <button
            onClick={() => handleComplete(row.id)}
            className="px-2 py-1 text-xs rounded-sm transition-colors"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          >
            Complete
          </button>
          <ReleaseButton
            assignmentId={row.id}
            onRelease={handleRelease}
            isLoading={releasingId === row.id}
            disabled={releasingId !== null}
            size="sm"
            brand={brand}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: brand.colors.bgLight }}>
      <AdminHeader
        title="My Work"
        subtitle={`${activeItems.length} items assigned to you`}
        brand={brand}
        Icons={Icons}
        rightContent={
          <div className="flex items-center gap-3">
            <span className="text-sm" style={{ color: brand.colors.textLight }}>Status:</span>
            <StatusToggle
              currentStatus={operatorStatus}
              onStatusChange={setOperatorStatus}
              brand={brand}
            />
          </div>
        }
      />

      {/* Summary Cards */}
      <div className="px-6 py-4 border-b" style={{ backgroundColor: 'white', borderColor: brand.colors.border }}>
        <div className="flex gap-4 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: brand.colors.bgLight }}>
            <span className="text-2xl font-bold" style={{ color: brand.colors.link }}>{activeItems.length}</span>
            <span className="text-sm" style={{ color: brand.colors.textLight }}>Active Items</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#D1FAE5' }}>
            <span className="text-2xl font-bold" style={{ color: '#059669' }}>
              {myWork.filter(i => i.status === 'locked').length}
            </span>
            <span className="text-sm" style={{ color: '#065F46' }}>Locked</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
            <span className="text-2xl font-bold" style={{ color: '#EF4444' }}>
              {myWork.filter(i => i.isBreached).length}
            </span>
            <span className="text-sm" style={{ color: '#B91C1C' }}>Breached</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#FEF3C7' }}>
            <span className="text-2xl font-bold" style={{ color: '#D97706' }}>
              {myWork.filter(i => i.isAtRisk && !i.isBreached).length}
            </span>
            <span className="text-sm" style={{ color: '#92400E' }}>At Risk</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <TabBar
        tabs={filterTabs}
        activeTab={activeFilter}
        onTabChange={setActiveFilter}
        brand={brand}
      />

      {/* Work Table */}
      <div className="flex-1 overflow-auto p-6">
        {/* Team Coverage Section */}
        {showTeamCoverage && colleagues.some(c => c.backlogCount > 0) && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => setShowTeamCoverage(!showTeamCoverage)}
                className="flex items-center gap-1 text-sm font-medium"
                style={{ color: brand.colors.textLight }}
              >
                <Icons.ChevronDown
                  className="w-4 h-4 transition-transform"
                  style={{ transform: showTeamCoverage ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                />
                Team Coverage
              </button>
            </div>
            <TeamCoverageSection
              colleagues={colleagues.filter(c => c.backlogCount > 0)}
              onClaimFromColleague={handleClaimFromColleague}
              claimingId={claimingFromColleagueId}
              brand={brand}
              Icons={Icons}
            />
          </div>
        )}

        {filteredItems.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-12 rounded-xl"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
          >
            <Icons.Check className="w-12 h-12 mb-3" style={{ color: brand.colors.success }} />
            <h3 className="text-lg font-medium mb-1" style={{ color: brand.colors.text }}>
              No work items
            </h3>
            <p className="text-sm mb-4" style={{ color: brand.colors.textLight }}>
              Claim items from the queue to get started
            </p>
            <button
              onClick={() => onNavigate('queue')}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              Go to Queue
            </button>
          </div>
        ) : (
          <div
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
          >
            <AdminDataTable
              columns={columns}
              data={filteredItems}
              brand={brand}
              Icons={Icons}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MyWork;
